import dayjs from 'dayjs';
import { useState } from 'react';
import CommonModal from '../../../extras/commonModal';
import toastWrapper from '../../../extras/toastify';
import { updateAlmaXExtension } from '../../../services/apiService';
import { styledDefaultColDef } from '../../../extras/cellRenderers';

const ReasonRenderer = (props) => {
  if (!props?.data?.reason) return <></>;
  return (
    <div className='flex items-center h-full'>
      <pre>{props?.data?.reason?.slice(0, 100)}</pre>
    </div>
  );
};

const UserCellRenderer = (props) => {
  const { firstName, lastName, thumbnail } = props?.data;
  return (
    <div className='flex items-center justify-start gap-4 h-full '>
      <div>
        <img
          src={thumbnail ?? 'https://www.w3schools.com/howto/img_avatar.png'}
          alt='Profile Picture'
          className='h-12 w-12 rounded-full'
        />
      </div>
      <div className='capitalize'>
        {firstName} {lastName}
      </div>
    </div>
  );
};

const ApproveModal = ({ approveModal, setApproveModal, data }) => {
  const [selectedOption, setSelectedOption] = useState('1');
  const handleSelectChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const confirmApprove = async () => {
    try {
      await updateAlmaXExtension({
        userId: data?.id,
        goalType: data?.courseType,
        adminStatus: 'approved',
        months: parseFloat(selectedOption),
      });
      toastWrapper('success', 'Approved Successfully', 'top-right');
      setApproveModal(false);
    } catch (error) {
      toastWrapper('error', 'Failed to Approve', 'top-right');
      console.error(error);
    }
  };

  return (
    <CommonModal
      isOpen={approveModal}
      setIsOpen={setApproveModal}
      removePadding
      classStyle='lg:w-5/12 md:w-7/12 w-10/12 max-h-80h overflow-y-auto rounded-md'
    >
      <div className='p-2 grid place-items-center'>
        <div className='space-y-2 text-center'>
          <div className='text-xl'>ABCAT Extension:</div>
          {data?.reason && (
            <pre className='whitespace-normal break-words'>
              Reason - {data?.reason}
            </pre>
          )}
          <div className='w-full space-y-2'>
            <select
              className='border border-solid border-gray-300 rounded-md p-2 w-full'
              value={selectedOption}
              onChange={handleSelectChange}
            >
              <option value='0.5'>15 days</option>
              <option value='1'>1 Month</option>
              <option value='2'>2 Months</option>
              <option value='3'>3 Months</option>
            </select>
            <button
              className='bg-brand-primary text-white p-2 rounded-md w-full'
              onClick={confirmApprove}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

const RejectModal = ({ rejectModal, setRejectModal, data }) => {
  const [rejectReason, setRejectReason] = useState('');
  const postRejectionReason = async () => {
    try {
      await updateAlmaXExtension({
        userId: data?.id,
        goalType: data?.courseType,
        adminStatus: 'rejected',
        rejectionReason: rejectReason,
      });
      toastWrapper('success', 'Rejected Successfully', 'top-right');
      setRejectModal(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <CommonModal
      isOpen={rejectModal}
      setIsOpen={setRejectModal}
      classStyle='rounded-md'
    >
      <div className='w-96 h-44 grid place-items-center px-2'>
        <div className='space-y-2 text-center'>
          <p className='text-xl'>Rejection Reason:</p>
          <textarea
            className='border border-solid border-gray-300 rounded-md p-2 w-full'
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <button
            className='bg-brand-primary text-white p-2 rounded-md w-full'
            onClick={postRejectionReason}
            disabled={rejectReason.length < 3}
          >
            Confirm
          </button>
        </div>
      </div>
    </CommonModal>
  );
};

const UserEmailRenderer = (props) => {
  const { email } = props?.data;
  return (
    <div className='flex items-center h-full'>
      <div>{email}</div>
    </div>
  );
};

const UserPhoneRenderer = (props) => {
  const { phone } = props?.data;
  return (
    <div className='flex items-center h-full'>
      <div>{phone}</div>
    </div>
  );
};

const UserMockTestRenderer = (props) => {
  const { mockTestLink } = props?.data;
  return (
    <div className='flex items-center h-full'>
      <div>{mockTestLink}</div>
    </div>
  );
};

const UserTestRenderer = (props) => {
  const { testLink } = props?.data;
  return (
    <div className='flex items-center h-full'>
      <div>{testLink}</div>
    </div>
  );
};

const UserExpiryDateRenderer = (props) => {
  const { expiryDate } = props?.data;
  return (
    <div className='flex items-center h-full'>
      <div>{dayjs(expiryDate).format('DD/MM/YYYY')}</div>
    </div>
  );
};

const UserNoOfExtensionRenderer = (props) => {
  const { noOfExtension } = props?.data;
  return (
    <div className='flex items-center h-full'>
      <div>{noOfExtension}</div>
    </div>
  );
};

export const columnDefinitions = [
  {
    headerName: 'Id',
    field: 'userId',
    hide: true,
  },
  {
    headerName: 'Name',
    field: 'name',
    cellRenderer: UserCellRenderer,
  },
  {
    headerName: 'Email',
    field: 'email',
    cellRenderer: UserEmailRenderer,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['equals'],
      suppressAndOrCondition: true,
    },
    valueGetter: (params) => {
      return `${params.data?.email}`;
    },
  },
  {
    headerName: 'Phone',
    field: 'phone',
    cellRenderer: UserPhoneRenderer,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Reason',
    field: 'reason',
    filter: 'agTextColumnFilter',
    cellRenderer: ReasonRenderer,
  },
  {
    headerName: 'Mock Test Link',
    field: 'mockTestLink',
    cellRenderer: UserMockTestRenderer,
  },
  {
    headerName: 'Test Link',
    field: 'testLink',
    cellRenderer: UserTestRenderer,
  },
  {
    headerName: 'Expiry Date',
    field: 'expiryDate',
    cellRenderer: UserExpiryDateRenderer,
  },
  {
    headerName: 'Number of Extension',
    field: 'numberOfExtension',
    cellRenderer: UserNoOfExtensionRenderer,
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: ['Equals'],
      suppressAndOrCondition: true,
    },
    valueGetter: (params) => {
      return `${params.data?.numberOfExtension}`;
    },
  },
  {
    headerName: 'Manage',
    field: 'manage',
    filter: false,
    cellRenderer: (props) => {
      const [approveModal, setApproveModal] = useState(false);
      const [rejectionModal, setRejectionModal] = useState(false);
      return (
        <div className='flex items-center justify-left h-full'>
          <div className='flex items-center justify-center gap-4'>
            <div
              className='cursor-pointer text-blue-c5'
              onClick={() => setApproveModal(true)}
            >
              Approve
            </div>
            <div
              className='cursor-pointer text-brand-primary'
              onClick={() => setRejectionModal(true)}
            >
              Reject
            </div>
          </div>
          <ApproveModal
            approveModal={approveModal}
            setApproveModal={setApproveModal}
            data={props?.data}
          />
          <RejectModal
            setRejectModal={setRejectionModal}
            rejectModal={rejectionModal}
            data={props?.data}
          />
        </div>
      );
    },
  },
];

export const defaultColDef = {
  ...styledDefaultColDef,
  filter: true,
};
